/* Device-level persistence for the NFC Solutions PWA.
 *
 * When the app is installed (or even when running in a browser tab),
 * we remember the business code and the role of THIS device so that
 * subsequent launches can skip the business-code screen and jump
 * straight to the right login form (or, if a session is still valid,
 * the dashboard itself via the existing role-session logic).
 *
 * Storage choice: localStorage.
 *   - Survives reloads / app reopens.
 *   - Survives across tabs (the operator might have the kitchen pinned
 *     in one window and the staff dashboard in another).
 *   - We deliberately do NOT store passwords here — only the business
 *     code (an identifier, not a secret) and a role label.
 *
 * Keys are namespaced with `nfcs:` so this never collides with anything
 * else on the origin.
 */

const K_BUSINESS_CODE = "nfcs:device:business_code";
const K_DEVICE_ROLE = "nfcs:device:role";
const K_DEVICE_ID = "nfcs:device:id";

// Roles that the device-setup flow treats as "installable" entry points.
// Customer-facing routes are intentionally excluded — the customer menu
// stays purely web-based.
export const INSTALLABLE_ROLES = ["owner", "staff", "kitchen", "bar", "admin", "retail"];

// Session 30.11 — Dashboards offered by the native app boot flow, in
// display order. "Last used" is remembered per device (see
// getLastUsedDashboard) but never locks the device to it.
export const NATIVE_DASHBOARDS = [
  { key: "bar", label: "Bar", path: "/bar", blurb: "Drinks tickets & bar station" },
  { key: "kitchen", label: "Kitchen", path: "/kitchen", blurb: "Food tickets & prep board" },
  { key: "staff", label: "Staff", path: "/staff", blurb: "Tables, orders & payments" },
  { key: "retail", label: "Retailer", path: "/retail", blurb: "Counter sales & tabs" },
  { key: "owner", label: "Owner / Manager", path: "/owner", blurb: "Overview, menu & settings" },
];
const K_LAST_DASHBOARD = "nfcs:device:last_dashboard";
export function getLastUsedDashboard() {
  const v = _safeGet(K_LAST_DASHBOARD);
  return v && NATIVE_DASHBOARDS.some((d) => d.key === v) ? v : "";
}
export function setLastUsedDashboard(key) {
  if (!NATIVE_DASHBOARDS.some((d) => d.key === key)) return;
  _safeSet(K_LAST_DASHBOARD, key);
  // Keep the legacy device-role in step so older code paths agree.
  try { setStoredDeviceRole(key); } catch (_) { /* noop */ }
}

function _safeGet(key) {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
}
function _safeSet(key, value) {
  try { window.localStorage.setItem(key, String(value)); } catch (_) { /* ignore quota / privacy mode */ }
}
function _safeRemove(key) {
  try { window.localStorage.removeItem(key); } catch (_) { /* noop */ }
}

export function getStoredBusinessCode() {
  const v = _safeGet(K_BUSINESS_CODE);
  return v ? v.trim() : "";
}
export function setStoredBusinessCode(code) {
  const v = String(code || "").trim().toLowerCase();
  if (!v) return;
  _safeSet(K_BUSINESS_CODE, v);
}

export function getStoredDeviceRole() {
  const v = _safeGet(K_DEVICE_ROLE);
  return v && INSTALLABLE_ROLES.includes(v) ? v : "";
}
export function setStoredDeviceRole(role) {
  if (!INSTALLABLE_ROLES.includes(role)) return;
  _safeSet(K_DEVICE_ROLE, role);
}

// A stable per-device identifier. Generated once on first launch and
// re-used forever. Useful when (Phase 2) the admin wants to manage
// installed devices remotely.
export function getDeviceId() {
  let id = _safeGet(K_DEVICE_ID);
  if (id) return id;
  // Prefer crypto.randomUUID where available (modern browsers + PWA
  // contexts); fall back to a quick-and-dirty random string otherwise.
  try {
    id = (window.crypto && typeof window.crypto.randomUUID === "function")
      ? window.crypto.randomUUID()
      : `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  } catch (_) {
    id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
  _safeSet(K_DEVICE_ID, id);
  return id;
}

export function clearDeviceConfig() {
  _safeRemove(K_BUSINESS_CODE);
  _safeRemove(K_DEVICE_ROLE);
  // We intentionally KEEP the device id — it's the stable identity used
  // for future remote-management, even after a local reset.
}

// Resolve where this device should "land" on launch. Returns one of the
// login route paths, or null if nothing has been configured yet (in
// which case the caller leaves the user on the public landing).
export function resolveLandingPathForDevice() {
  const role = getStoredDeviceRole();
  switch (role) {
    case "owner": return "/owner";
    case "staff": return "/staff";
    case "kitchen": return "/kitchen";
    case "bar": return "/bar";
    case "admin": return "/admin";
    case "retail": return "/retail";
    default: return null;
  }
}

// True when the app is running in standalone display mode (i.e. it was
// installed and launched from the home screen / start menu rather than
// being opened as a normal browser tab).
//
// Also returns true when the app is running inside the Capacitor
// Android APK — from the React app's perspective a native shell is a
// stricter form of "installed standalone", so every code path that
// gates on "is this an installed launcher-invoked launch?" should
// treat both the same. This is how the PWA install/auto-install
// flows correctly bypass themselves inside the APK.
export function isStandaloneLaunch() {
  if (typeof window === "undefined") return false;
  try {
    // Capacitor native shell → treat as installed.
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform()) return true;
    // Session 27.68 — Tauri EXE shell. The desktop wrapper injects
    // `window.__TAURI__` (v1) and `window.__TAURI_INTERNALS__` (v2).
    // Without this check the EXE was falling through to the customer
    // "Scan required. Open the menu from a tag." landing page instead
    // of the role-picker install page.
    if (window.__TAURI__ || window.__TAURI_INTERNALS__) return true;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true; // iOS Safari
    return false;
  } catch (_) {
    return false;
  }
}

// True when the app is running inside a native/installed container
// where the Coordinator role makes sense — Capacitor APK or Tauri EXE
// ONLY. Regular browser tabs return false so the Coordinator
// election prompt / heartbeat never runs in a browser tab (per the
// Session 27.53 spec: "everything we're about to do is going to be
// strictly for the applications only").
//
// Session 27.55 tightening: PWA standalone (`display-mode:
// standalone`) is NO LONGER treated as installed. The owner made a
// clear call — the "web app" middle ground is being retired
// entirely and only APK / EXE downloads will be offered from the
// admin. Users who tap "Add to Home Screen" in Chrome/Edge get a
// glorified bookmark, which is fine for browsing — they will not
// receive the Coordinator prompt or any local-first behaviour that
// would create expectations the browser can't deliver on. Kept as a
// separate helper from `isStandaloneLaunch()` so the existing
// offline-auth / snapshot code that legitimately triggers on
// display-mode: standalone (that's a different, older contract)
// isn't affected.
export function isInstalledAppEnvironment() {
  if (typeof window === "undefined") return false;
  try {
    // Capacitor Android / iOS shell.
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform()) return true;
    // Tauri EXE shell — Tauri injects `window.__TAURI__` (and, since
    // Tauri v2, `window.__TAURI_INTERNALS__`). We check both to be
    // resilient across major versions when we ship the Windows build.
    if (window.__TAURI__ || window.__TAURI_INTERNALS__) return true;
    return false;
  } catch (_) {
    return false;
  }
}

// Best-effort "which shell are we in?" tag reported to the backend on
// coordinator claim so an admin can later tell whether the Coordinator
// for a given day was running on the APK, the EXE or a PWA.
export function getInstalledPlatformTag() {
  if (typeof window === "undefined") return "unknown";
  try {
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform()) {
      const p = (window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || "capacitor";
      return p === "android" ? "apk" : (p === "ios" ? "ios" : "capacitor");
    }
    if (window.__TAURI__ || window.__TAURI_INTERNALS__) return "exe";
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return "pwa";
    return "browser";
  } catch (_) {
    return "unknown";
  }
}

// Human-readable label for THIS device, best-effort. Used as the
// Coordinator's `device_label` in the DB so an admin looking at a
// crashed day can tell "the Sunset Tacos coordinator on 2026-08-02
// was the Owner iPad" without decoding a UUID.
export function getDeviceLabel() {
  if (typeof window === "undefined") return "Device";
  try {
    // Prefer navigator.userAgentData if available (Chromium 90+).
    const ua = navigator.userAgent || "";
    let platform = "";
    if (/iPad/i.test(ua)) platform = "iPad";
    else if (/iPhone/i.test(ua)) platform = "iPhone";
    else if (/Android/i.test(ua)) platform = /Tablet|SM-T/i.test(ua) ? "Android Tablet" : "Android";
    else if (/Windows/i.test(ua)) platform = "Windows PC";
    else if (/Macintosh/i.test(ua)) platform = "Mac";
    else if (/Linux/i.test(ua)) platform = "Linux";
    else platform = "Device";
    const role = getStoredDeviceRole();
    return role ? `${platform} · ${role.charAt(0).toUpperCase()}${role.slice(1)}` : platform;
  } catch (_) {
    return "Device";
  }
}
